/**
 * 좌측 사이드바 (Stripe Dashboard형 — 1단 도메인 나열)
 * UI_STANDARD_APPLICATION_PLAN.md §3.1, §5 Phase 1.1
 * PRODUCTIZATION_PROGRESS.md §3.1: 역할별 메뉴 필터(useMenuVisibility)
 * Shell dedup: 브랜드·검색은 AppTopBar 전용 · 아코디언 openKeys
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, Menu, type MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  type AppMenuItem,
  SIDEBAR_DOMAIN_ITEMS,
  SIDEBAR_FOOTER_ITEMS,
  filterAdminOnlyItems,
  filterPilotFoldedMenuItems,
  findMenuKeyPath,
  getSidebarOpenKeys,
  getSidebarSelectedKey,
  openKeysFromMenuPath,
} from '@frontend/config/appMenuConfig';
import { isPurposeOrDomainMenuKeyVisible } from '@shared/frontend-experience-architecture-and-navigation-canon';
import { useMenuVisibility } from '../../hooks/ui/useMenuVisibility';
import { usePilotSurfaces } from '../../hooks/ui/usePilotSurfaces';

const { Sider } = Layout;

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;

export interface AppSidebarProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** 메뉴 클릭 후 호출 (모바일 Drawer 닫기 등) */
  onNavigate?: () => void;
  /** 모바일 Drawer 내부 — 그룹 라벨·터치 타깃 확대 (Stream D7) */
  inDrawer?: boolean;
}

function filterMenuByVisibility(
  items: AppMenuItem[],
  visibleDomainKeys: string[],
  canAccessPath: (path: string) => boolean,
  parentDomainVisible = false
): AppMenuItem[] {
  const isDomainKeyVisible = (key: string) =>
    isPurposeOrDomainMenuKeyVisible(key, visibleDomainKeys);

  return items.flatMap((item) => {
    const kids = item.children ?? [];
    const selfDomainVisible = isDomainKeyVisible(item.key);
    if (kids.length > 0) {
      const childParentVisible = selfDomainVisible || parentDomainVisible;
      const filteredKids = filterMenuByVisibility(
        kids,
        visibleDomainKeys,
        canAccessPath,
        childParentVisible
      );
      if (selfDomainVisible) {
        if (filteredKids.length > 0) {
          return [{ ...item, children: filteredKids }];
        }
        if (item.path) {
          const { children: _children, ...itemWithoutChildren } = item;
          return [itemWithoutChildren];
        }
        return [];
      }
      if (filteredKids.length === 0) {
        return [];
      }
      return [{ ...item, children: filteredKids }];
    }
    if (selfDomainVisible) {
      return [item];
    }
    if (parentDomainVisible && item.path && canAccessPath(item.path)) {
      return [item];
    }
    return [];
  });
}

function createAccordionOpenChange(
  items: AppMenuItem[],
  currentOpenKeys: string[],
  setOpenKeys: React.Dispatch<React.SetStateAction<string[]>>
): (openKeys: string[]) => void {
  return (keys) => {
    const latestOpenKey = keys.find((key) => !currentOpenKeys.includes(key));
    if (latestOpenKey) {
      const path = findMenuKeyPath(items, latestOpenKey);
      setOpenKeys(path ? openKeysFromMenuPath(path, items) : keys);
      return;
    }
    setOpenKeys(keys);
  };
}

export const AppSidebar: React.FC<AppSidebarProps> = ({
  collapsed = false,
  onCollapsedChange,
  onNavigate,
  inDrawer = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { visibleDomainKeys, canAccessPath, hasFullMenuAccess } = useMenuVisibility();
  const pilotSurfaces = usePilotSurfaces();
  const selectedKey = getSidebarSelectedKey(location.pathname);

  const domainItemsFiltered = useMemo(
    () =>
      filterMenuByVisibility(
        filterPilotFoldedMenuItems(
          filterAdminOnlyItems(SIDEBAR_DOMAIN_ITEMS, hasFullMenuAccess),
          pilotSurfaces
        ),
        visibleDomainKeys,
        canAccessPath
      ),
    [visibleDomainKeys, canAccessPath, hasFullMenuAccess, pilotSurfaces]
  );
  const footerItemsFiltered = useMemo(
    () =>
      filterMenuByVisibility(
        filterPilotFoldedMenuItems(
          filterAdminOnlyItems(SIDEBAR_FOOTER_ITEMS, hasFullMenuAccess),
          pilotSurfaces
        ),
        visibleDomainKeys,
        canAccessPath
      ),
    [visibleDomainKeys, canAccessPath, hasFullMenuAccess, pilotSurfaces]
  );

  const [domainOpenKeys, setDomainOpenKeys] = useState<string[]>([]);
  const [footerOpenKeys, setFooterOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    const { domainOpenKeys: nextDomain, footerOpenKeys: nextFooter } = getSidebarOpenKeys(
      location.pathname
    );
    const domainPath = findMenuKeyPath(domainItemsFiltered, selectedKey);
    const footerPath = findMenuKeyPath(footerItemsFiltered, selectedKey);
    setDomainOpenKeys(
      domainPath ? openKeysFromMenuPath(domainPath, domainItemsFiltered) : nextDomain
    );
    setFooterOpenKeys(
      footerPath ? openKeysFromMenuPath(footerPath, footerItemsFiltered) : nextFooter
    );
  }, [location.pathname, selectedKey, domainItemsFiltered, footerItemsFiltered]);

  const handleDomainOpenChange = useCallback<(openKeys: string[]) => void>(
    createAccordionOpenChange(domainItemsFiltered, domainOpenKeys, setDomainOpenKeys),
    [domainItemsFiltered, domainOpenKeys]
  );
  const handleFooterOpenChange = useCallback<(openKeys: string[]) => void>(
    createAccordionOpenChange(footerItemsFiltered, footerOpenKeys, setFooterOpenKeys),
    [footerItemsFiltered, footerOpenKeys]
  );

  const flattenMenuItems = (items: AppMenuItem[]): AppMenuItem[] =>
    items.flatMap((item) => [item, ...flattenMenuItems(item.children ?? [])]);

  type AntMenuItem = NonNullable<MenuProps['items']>[number];

  const toAntMenuItem = (item: AppMenuItem): AntMenuItem => ({
    key: item.key,
    icon: item.icon,
    label: item.label,
    ...(item.type === 'group' ? { type: 'group' as const } : {}),
    children: item.children?.length ? item.children.map(toAntMenuItem) : undefined,
  });

  const domainMenuItems = domainItemsFiltered.map(toAntMenuItem);
  const footerMenuItems = footerItemsFiltered.map(toAntMenuItem);

  const allItemsFlat = flattenMenuItems([...domainItemsFiltered, ...footerItemsFiltered]);
  const getPath = (key: string) =>
    allItemsFlat.find((item) => item.key === key)?.path ?? '/entity/internal';

  const handleClick = ({ key }: { key: string }) => {
    const target = allItemsFlat.find((item) => item.key === key);
    if (!target || (target.children?.length ?? 0) > 0) {
      return;
    }
    navigate(getPath(key));
    onNavigate?.();
  };

  return (
    <Sider
      className={inDrawer ? 'app-sidebar app-sidebar--drawer' : 'app-sidebar'}
      theme="dark"
      collapsible
      width={SIDEBAR_WIDTH}
      collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
      collapsed={collapsed}
      {...(onCollapsedChange !== undefined ? { onCollapse: onCollapsedChange } : {})}
      role="navigation"
      aria-label="앱 사이드바 네비게이션"
    >
      {inDrawer && (
        <div className="app-sidebar__group-label" id="app-sidebar-domain-label">
          도메인
        </div>
      )}
      <Menu
        theme="dark"
        mode="inline"
        className="app-sidebar__menu app-sidebar__menu--domain"
        selectedKeys={selectedKey ? [selectedKey] : []}
        openKeys={domainOpenKeys}
        onOpenChange={handleDomainOpenChange}
        items={domainMenuItems}
        onClick={handleClick}
        {...(inDrawer ? { 'aria-labelledby': 'app-sidebar-domain-label' } : {})}
      />
      <div className="app-sidebar__divider" />
      {inDrawer && (
        <div className="app-sidebar__group-label" id="app-sidebar-footer-label">
          플랫폼
        </div>
      )}
      <Menu
        theme="dark"
        mode="inline"
        className="app-sidebar__menu app-sidebar__menu--footer"
        selectedKeys={selectedKey ? [selectedKey] : []}
        openKeys={footerOpenKeys}
        onOpenChange={handleFooterOpenChange}
        items={footerMenuItems}
        onClick={handleClick}
        {...(inDrawer ? { 'aria-labelledby': 'app-sidebar-footer-label' } : {})}
      />
    </Sider>
  );
};

export default AppSidebar;
